import { ProjectHeader } from '@/components/project-header';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { queryClient } from '@/providers/query-provider';
import {
  AppointmentItem,
  AppointmentQuery,
  getAppointments,
} from '@/lib/getProjectAppointments';
import { getProjectDetails } from '@/lib/getProject';
import { Spinner } from '@/components/ui/spinner';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Calendar, FilePen } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { t } from 'i18next';
import { useTranslation } from 'react-i18next';
import { formatTime } from '@/lib/utils';
import { getImagesByAppointmentId } from '@/services/imageCalls';
import ImageGallery from '@/components/image-gallery';
import { useMemo } from 'react';
import { CDN_URL, supabase } from '@/services';

async function loadProjectDetails(projectId: string): Promise<ProjectDetails> {
  const project = await getProjectDetails(projectId);
  const { upcoming, today, completed: previous } = await getAppointments(projectId);
  return {
    project,
    appointments: {
      upcoming,
      today,
      completed: previous,
    },
  };
}

async function getProjectAgreement(projectId: string): Promise<Blob | null> {
  const { data, error } = await supabase.storage
    .from('files')
    .download(`agreements/${projectId}`);
  if (error) {
    console.error('Error downloading file: ', error.message);
    throw error;
  }
  return data;
}
export async function ProjectLoader(projectId: string) {
  return queryClient.fetchQuery({
    queryKey: ['project', projectId],
    queryFn: () => loadProjectDetails(projectId),
    staleTime: 1000,
  });
}

type ProjectDetails = {
  project: ProjectTable;
  appointments: AppointmentQuery;
};

export function ProjectPage() {
  const { projectId } = useParams();
  const { data } = useQuery<ProjectDetails>({
    networkMode: 'offlineFirst',
    queryKey: ['project', projectId],
    queryFn: () => loadProjectDetails(projectId!),
  });
  console.log(data);
  if (!data)
    return (
      <div className="flex-1 md:p-6">
        <Spinner size="xl" />
      </div>
    );
  const handleAgreement = async () => {
    const blob = await getProjectAgreement(projectId!);
    if (!blob) return;
    const a = document.createElement('a');
    const url = window.URL.createObjectURL(blob);
    a.href = url;
    a.download = 'Rise Renovation Agreement.pdf';
    a.click();
    window.URL.revokeObjectURL(url);
  };
  const piiDate = formatTime(data.project.pii_date ?? '');

  return (
    <div className="flex-1 md:p-4 ">
      <div className="mb-2">
        <Button variant="ghost" asChild className="pl-0 text-muted-foreground">
          <Link to="/">
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('backToHome')}
          </Link>
        </Button>
      </div>

      <ProjectHeader currentStatus={data.project.status ?? ''} />

      <div className=" flex justify-between items-center mt-4">
        {data.project?.pii_date && (
          <div className="mb-4">
            <h3 className="text-md font-semibold">PII:</h3>
            <p className="">{piiDate}</p>
          </div>
        )}
        <div className="flex items-center">
          <FilePen className="h-6 w-6" />
          <Button
            onClick={handleAgreement}
            className="text-muted-foreground px-1  hover:underline self-start"
            variant="link"
          >
            {t('downloadagreement')}
          </Button>
        </div>
      </div>
      {data.project?.notes?.length && (
        <div className="mb-4">
          <h3 className="text-md font-semibold">{t('scope')}:</h3>
          <p className="">{data.project.notes}</p>
        </div>
      )}

      <div className="flex flex-col md:flex-row md:items-center justify-between gap-2 my-4">
        <h2 className="text-lg font-bold">{t('appointments')}</h2>
        <div className="flex gap-x-2">
          <Badge
            variant="outline"
            className="bg-green-50  text-green-600 border-green-100"
          >
            {t('completed')}: {data.appointments.completed.length}
          </Badge>
          <Badge
            variant="outline"
            className="bg-yellow-50  text-yellow-600 border-yellow-100"
          >
            {t('upcoming')}: {data.appointments.upcoming.length}
          </Badge>
        </div>
      </div>

      {data.appointments.today.length > 0 && (
        <div>
          <p className=" my-2 font-semibold">
            {t('today')} ({data.appointments.upcoming.length})
          </p>
          {data.appointments.today.map((appoint) => (
            <div key={appoint.appointment_id}>
              <ProjectAppointment data={appoint} />
            </div>
          ))}
        </div>
      )}

      {data.appointments.upcoming.length > 0 && (
        <div>
          <p className="my-2 font-semibold">
            {t('upcoming')} ({data.appointments.upcoming.length})
          </p>
          {data.appointments.upcoming.map((appoint) => (
            <div key={appoint.appointment_id}>
              <ProjectAppointment data={appoint} />
            </div>
          ))}
        </div>
      )}

      {data.appointments.completed.length > 0 && (
        <div>
          <p className=" my-2 font-semibold">
            {t('completed')} ({data.appointments.completed.length})
          </p>
          {data.appointments.completed.map((appoint) => (
            <div key={appoint.appointment_id}>
              <ProjectAppointment data={appoint} />
            </div>
          ))}
        </div>
      )}
      {!data.appointments.today.length &&
        !data.appointments.upcoming.length &&
        !data.appointments.completed.length && (
          <h3 className=" font-semibold">{t('noAppointments')}</h3>
        )}
    </div>
  );
}

const ProjectAppointment = ({ data: row }: { data: AppointmentItem }) => {
  const installerName = row?.users?.first_name + ' ' + row?.users?.last_name;
  const initials = installerName
    .split(' ')
    .map((n) => n[0])
    .join('');

  const images = useQuery({
    networkMode: 'offlineFirst',
    queryKey: ['images', row.appointment_id],
    queryFn: () => getImagesByAppointmentId(row.appointment_id!),
  });
  const { t } = useTranslation();
  function getBadgeClass(status: string) {
    switch (status) {
      case 'Scheduled':
        return 'bg-yellow-50 text-yellow-600 border-yellow-100';
      case 'Completed':
        return 'bg-green-50 text-green-600 border-green-100';
      default:
        return 'bg-gray-50 text-gray-600 border-gray-100';
    }
  }

  const imageTable = useMemo((): ImageTable[] => {
    if (!images.data) return [];
    if (Array.isArray(images.data) && images.data?.length > 0) {
      return images.data.map((e) => ({
        ...e,
        image_url: `${CDN_URL}/object/public/${e.image_url}`,
      }));
    }
    return [];
  }, [images.data]);

  return (
    <Card key={row.appointment_id} className="my-2">
      <CardContent className="p-4">
        <div className="grid gap-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-xs " />
              <span className="font-medium">
                {formatTime(row.appointment_time || '')}
              </span>
              {row.end_date && (
                <>
                  <span className="text-muted-foreground"> - </span>
                  <span className="font-medium">{formatTime(row.end_date || '')}</span>
                </>
              )}
            </div>
            <Badge variant="outline" className={` ${getBadgeClass(row.status!)}`}>
              {t((row.status ?? '').replace(/\s/g, '').toLowerCase())}
            </Badge>
          </div>
          <div>
            <h3 className="font-semibold mb-1">{t('installer')}</h3>
            <div className="flex items-center gap-2">
              <Avatar className="h-8 w-8">
                <AvatarImage src="/placeholder.svg" alt="Emily Johnson" />
                <AvatarFallback>{initials}</AvatarFallback>
              </Avatar>
              <span>{installerName}</span>
            </div>
          </div>
          <div>
            <h3 className="font-semibold mb-1">{t('scope')}</h3>
            <p className="text-sm ">{row.scope}</p>
          </div>
          <div>
            {images.data && images.data?.length > 0 && (
              <ImageGallery images={imageTable} />
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
